"use client";

import { formatImageUrl } from "@alba-cars/common-modules";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { LogOut, Phone, User } from "lucide-react";
import dynamic from "next/dynamic";
import { Link } from "@/i18n/routing";
import { usePathname, useRouter } from "@/i18n/routing";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

import OptimizedImage from "@/components/common/images/OptimizedImage";
import PhoneNumberModal from "@/components/common/modals/PhoneNumberModal";
import Icon from "@/components/Icons/Icon";
import IconHeart from "@/components/Icons/IconHeart";
import IconUser from "@/components/Icons/IconUser";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/contexts/DialogContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { initiateCallUs } from "@/lib/analytics/openai-events";
import { cn } from "@/lib/utils";
import { phoneNumber } from "@/utils/constants";
import LanguageSwitcher from "./LanguageSwitcher";

// Login form pulls in phone parsing, react-select and react-hook-form; load it only when the dialog opens.
const AuthDialog = dynamic(() => import("@/components/auth/AuthDialog"), {
  ssr: false,
  // Mirrors AuthDialog + AuthForm layout (title, agent toggle, phone field, button) so nothing jumps when it loads.
  loading: () => (
    <div className="w-full space-y-4 px-8 py-8 md:px-16" role="status" aria-busy="true" aria-label="Loading">
      <div className="flex flex-col items-center gap-2">
        <div className="h-8 w-72 max-w-full animate-pulse rounded-md bg-gray-200" />
        <div className="h-5 w-56 max-w-full animate-pulse rounded-md bg-gray-200" />
      </div>
      <div className="mx-auto w-full space-y-4 md:max-w-[80%]">
        <div className="flex items-center justify-between gap-3 rounded-2xl bg-secondary-light px-4 py-3">
          <div className="h-4 w-40 animate-pulse rounded-md bg-gray-200" />
          <div className="h-6 w-11 animate-pulse rounded-full bg-gray-200" />
        </div>
        <div className="h-[52px] w-full animate-pulse rounded-2xl bg-secondary-light" />
        <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200" />
      </div>
    </div>
  ),
});

const NavUserActions = () => {
  const { openDialog } = useDialog();
  const locale = useLocale();
  const isRTL = locale === "ar";
  const t = useTranslations("Navigation");

  const isTabletOrLarger = useMediaQuery("(min-width: 768px)");
  const { user, isAuthenticated, profile, isProfileLoading, agentAccess, logout } = useAuth();
  const [isPhoneNumberModalOpen, setIsPhoneNumberModalOpen] = useState(false);

  const pathname = usePathname();
  const router = useRouter();

  const isProfilePage = pathname === "/my-profile";
  const isLoginPage = pathname === "/login";

  const profilePicture = profile?.photo ? formatImageUrl(profile.photo) : "";

  const handleUserIconClick = () => {
    if (!user && !isLoginPage) {
      openDialog({
        content: <AuthDialog />,
      });
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Call button */}
      <div>
        <button
          onClick={() => {
            initiateCallUs({
              phone: phoneNumber,
              isDesktop: isTabletOrLarger,
              onOpenModal: () => setIsPhoneNumberModalOpen(true),
              source: "nav_call_button",
            });
          }}
          className="flex items-center rounded-20 bg-blue p-3 transition-all duration-300 hover:cursor-pointer hover:bg-[#345A8C33]"
          aria-label="Call us"
          tabIndex={0}
        >
          <span className="inter p-2">
            <Phone className="h-4 w-4 text-primary" strokeWidth={2.5} fill="#FF3448" />
          </span>
        </button>
      </div>

      {/* Favorites and Profile buttons */}
      <div className={cn("hidden xl:flex", locale === "ar" ? "space-x-2 space-x-reverse" : "space-x-2")}>
        <Link
          href={`/favorites?source=nav${isAuthenticated ? "&authenticated=true" : ""}`}
          className={cn(
            "flex items-center rounded-20 bg-blue transition-all duration-300 hover:cursor-pointer hover:bg-[#345A8C33]",
            isRTL ? "mr-2" : "ml-2",
          )}
        >
          <span className="sr-only">{t("favorites")}</span>
          <Icon icon={<IconHeart fill="#FF3448" />} width={14} />
        </Link>
        {user ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <div
                className={cn(
                  "flex max-h-14 max-w-14 items-center rounded-20 bg-blue transition-all duration-300 hover:cursor-pointer hover:bg-[#345A8C33]",
                  !profilePicture && "p-2",
                )}
                aria-label={t("profile")}
                aria-controls="user-menu"
                aria-haspopup="menu"
                aria-expanded="false"
              >
                <span className="sr-only">{t("profile")}</span>
                {isProfileLoading ? (
                  <div className="h-14 w-14 animate-pulse rounded-full bg-gray-200" />
                ) : profilePicture ? (
                  <OptimizedImage
                    src={profilePicture}
                    alt={`${user.name}'s profile image`}
                    className="h-14 w-14 rounded-full object-cover object-center p-2"
                    imageLayout="custom"
                    sizes="56px"
                    width={56}
                    height={64}
                  />
                ) : (
                  <div className="inter flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-primary p-2 text-white">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-[8000] min-w-[200px] rounded-2xl bg-white shadow-sm data-[side=bottom]:animate-slideDown data-[side=top]:animate-slideUp"
                sideOffset={8}
                avoidCollisions
                role="menu"
                aria-label={t("profile")}
                aria-orientation="vertical"
              >
                {!isProfilePage && !agentAccess.isSalesAgent ? (
                  <>
                    <DropdownMenu.Item
                      className="hover:gray-100 flex items-center space-x-4 rounded-2xl border-none p-4 outline-none transition-all duration-300 hover:cursor-pointer focus:bg-gray-100 focus:outline-none"
                      role="menuitem"
                      tabIndex={0}
                      onSelect={() => router.push("/my-profile")}
                    >
                      <User className="h-4 w-4" />
                      <span className="inter font-semibold text-black">{t("myProfile")}</span>
                    </DropdownMenu.Item>
                    <hr className="mx-4" aria-orientation="horizontal" />
                  </>
                ) : null}
                <DropdownMenu.Item
                  className="hover:gray-100 flex items-center space-x-4 rounded-2xl border-none p-4 outline-none transition-all duration-300 hover:cursor-pointer focus:bg-gray-100 focus:outline-none"
                  role="menuitem"
                  tabIndex={0}
                  onSelect={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="inter font-semibold text-black">{t("logout")}</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : (
          <div
            onClick={handleUserIconClick}
            className="flex items-center rounded-20 bg-blue transition-all duration-300 hover:cursor-pointer hover:bg-[#345A8C33]"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleUserIconClick();
              }
            }}
          >
            <span className="sr-only">{t("profile")}</span>
            <Icon icon={<IconUser />} width={14} height={14} />
          </div>
        )}
        <div className="hidden ultrawide:block">
          <LanguageSwitcher />
        </div>
      </div>
      <PhoneNumberModal
        isOpen={isPhoneNumberModalOpen}
        onClose={() => setIsPhoneNumberModalOpen(false)}
        phoneNumber={phoneNumber}
        trackingSource="nav_call_button"
      />
    </>
  );
};

export default NavUserActions;
